import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import AuthCallback from "@/components/AuthCallback";
import { Toaster } from "@/components/ui/sonner";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Transactions from "@/pages/Transactions";
import Expenses from "@/pages/Expenses";
import AdminReports from "@/pages/AdminReports";
import Reports from "@/pages/Reports";
import CashBank from "@/pages/CashBank";
import Users from "@/pages/Users";
import Suppliers from "@/pages/Suppliers";
import DeletionApprovals from "@/pages/DeletionApprovals";
import Bills from "@/pages/Bills";
import Customers from "@/pages/Customers";
import CustomerBills from "@/pages/CustomerBills";
import Kasbon from "@/pages/Kasbon";
import Deliveries from "@/pages/Deliveries";
import SupplierPortal from "@/pages/SupplierPortal";
import SupplierTempoSettings from "@/pages/SupplierTempoSettings";
import Reminders from "@/pages/Reminders";
import Cashback from "@/pages/Cashback";
import CashbackDisbursements from "@/pages/CashbackDisbursements";
import Profile from "@/pages/Profile";
import DataManagement from "@/pages/DataManagement";
import Deposits from "@/pages/Deposits";
import Attendance from "@/pages/Attendance";
import AttendanceDashboard from "@/pages/AttendanceDashboard";
import AttendanceSettings from "@/pages/AttendanceSettings";
import AttendanceSchedule from "@/pages/AttendanceSchedule";
import AttendanceRequests from "@/pages/AttendanceRequests";
import "@/App.css";

const Loader = () => (
  <div className="min-h-screen grid place-items-center bg-background">
    <div className="h-8 w-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
  </div>
);

const LAST_ROUTE_KEY = "last_route";

function RouteTracker() {
  const location = useLocation();
  useEffect(() => {
    const p = location.pathname;
    if (p !== "/" && p !== "/login" && !location.hash?.includes("session_id=")) {
      try { localStorage.setItem(LAST_ROUTE_KEY, p); } catch (e) { /* abaikan */ }
    }
  }, [location]);
  return null;
}

function isReloadNavigation() {
  try {
    const nav = performance.getEntriesByType?.("navigation")?.[0];
    if (nav) return nav.type === "reload";
    return performance.navigation?.type === 1;
  } catch (e) {
    return false;
  }
}

function Protected({ children, roles, perm, kperm }) {
  const { user, loading } = useAuth();
  if (loading) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to={homeFor(user.role)} replace />;
  if (perm && user.role !== "owner" && !user[perm]) return <Navigate to={homeFor(user.role)} replace />;
  if (kperm && user.role === "karyawan" && !user.can_full_access && !user[kperm]) return <Navigate to={homeFor(user.role)} replace />;
  return <Layout>{children}</Layout>;
}

function homeFor(role) {
  if (role === "supplier") return "/portal";
  if (role === "karyawan") return "/attendance";
  return "/dashboard";
}

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  // Saat REFRESH, browser HP/WebView bisa me-reset URL ke "/". Kembalikan ke halaman terakhir.
  if (isReloadNavigation()) {
    try {
      const last = localStorage.getItem(LAST_ROUTE_KEY);
      if (last && last !== "/" && last !== "/login") return <Navigate to={last} replace />;
    } catch (e) { /* abaikan */ }
  }
  return <Navigate to={homeFor(user.role)} replace />;
}

function LoginRoute() {
  const { user, loading } = useAuth();
  if (loading) return <Loader />;
  if (user) return <Navigate to={homeFor(user.role)} replace />;
  return <Login />;
}

function AppRouter() {
  const location = useLocation();
  // CRITICAL: deteksi session_id dari OAuth secara sinkron saat render (bukan useEffect) untuk mencegah race condition.
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
          <Route path="/" element={<RootRedirect />} />
          <Route path="/login" element={<LoginRoute />} />
          <Route path="/dashboard" element={<Protected roles={["owner", "admin"]}><Dashboard /></Protected>} />
          <Route path="/transactions" element={<Protected roles={["owner", "admin", "karyawan"]} kperm="can_create_order"><Transactions /></Protected>} />
          <Route path="/attendance" element={<Protected roles={["owner", "admin", "karyawan"]}><Attendance /></Protected>} />
          <Route path="/attendance/dashboard" element={<Protected roles={["owner"]}><AttendanceDashboard /></Protected>} />
          <Route path="/attendance/schedule" element={<Protected roles={["owner"]}><AttendanceSchedule /></Protected>} />
          <Route path="/attendance/requests" element={<Protected roles={["owner"]}><AttendanceRequests /></Protected>} />
          <Route path="/attendance/settings" element={<Protected roles={["owner"]}><AttendanceSettings /></Protected>} />
          <Route path="/expenses" element={<Protected roles={["owner", "admin"]}><Expenses /></Protected>} />
          <Route path="/admin-reports" element={<Protected roles={["owner", "admin"]}><AdminReports /></Protected>} />
          <Route path="/deliveries" element={<Protected roles={["owner", "admin", "karyawan"]} kperm="can_create_order"><Deliveries /></Protected>} />
          <Route path="/customers" element={<Protected roles={["owner", "admin"]}><Customers /></Protected>} />
          <Route path="/customer-bills" element={<Protected roles={["owner", "admin", "karyawan"]} perm="can_manage_billing"><CustomerBills /></Protected>} />
          <Route path="/reports" element={<Protected roles={["owner", "admin"]}><Reports /></Protected>} />
          <Route path="/reminders" element={<Protected roles={["owner", "admin"]}><Reminders /></Protected>} />
          <Route path="/cashback" element={<Protected roles={["owner", "admin"]}><Cashback /></Protected>} />
          <Route path="/cashback/disbursements" element={<Protected roles={["owner", "admin"]}><CashbackDisbursements /></Protected>} />
          <Route path="/cash-bank" element={<Protected roles={["owner", "admin"]} perm="can_view_cash_bank"><CashBank /></Protected>} />
          <Route path="/kasbon" element={<Protected roles={["owner", "admin"]}><Kasbon /></Protected>} />
          <Route path="/bills" element={<Protected roles={["owner", "admin"]} perm="can_pay_supplier"><Bills /></Protected>} />
          <Route path="/users" element={<Protected roles={["owner"]}><Users /></Protected>} />
          <Route path="/suppliers" element={<Protected roles={["owner"]}><Suppliers /></Protected>} />
          <Route path="/deposits" element={<Protected roles={["owner", "admin"]}><Deposits /></Protected>} />
          <Route path="/deletion-approvals" element={<Protected roles={["owner", "admin"]}><DeletionApprovals /></Protected>} />
          <Route path="/settings/supplier-tempo" element={<Protected roles={["owner"]}><SupplierTempoSettings /></Protected>} />
          <Route path="/settings/data" element={<Protected roles={["owner", "admin"]} perm="can_delete_data"><DataManagement /></Protected>} />
          <Route path="/portal" element={<Protected roles={["supplier"]}><SupplierPortal /></Protected>} />
          <Route path="/profile" element={<Protected roles={["owner", "admin", "karyawan", "supplier"]}><Profile /></Protected>} />
          <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <RouteTracker />
        <AppRouter />
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </AuthProvider>
  );
}

export default App;
